<template>
	<core-layout-content medium>
		<!-- Headline -->
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("plan") }}</template>
		</core-layout-headline-and-actions>

		<!-- Loading -->
		<j-loading-toast v-if="isLoading" />

		<!-- Language selector -->
		<div class="xs-4 mb4" v-if="$store.state.settings.allowedLocales">
			<div class="pr2 xs-12 fs4 lh125 fw7 pb1">{{ $t("language") }}</div>
			<core-select-locale
				class="xs-12"
				size="xsmall"
				v-model="selectedLocale"
			/>
		</div>

		<div v-if="data" class="mt4">
			<plan-info
				:data="data"
				:selectedLocale="selectedLocale"
				@plan:updated="fetchPlanDetail"
			></plan-info>

			<pricing-logic :data="data" @onRefresh="fetchPlanDetail"></pricing-logic>

			<!-- Tags -->
			<div class="flex xs-12 xs-between mt4">
				<div class="fs5 lh125 fw7 c1 mb2">{{ $t("tags") }}</div>
				<j-button @click="$modal.show('addTag')" type="tertiary">
					<crm-icon name="add" size="small" color="c2" />
					{{ $t("add") }}
				</j-button>
			</div>
			<j-card v-if="tags.length === 0" class="mb2 bg4--l3 p4 ta2 fs3 c6">{{
				$t("errors.no_data")
			}}</j-card>
			<j-card v-else class="flex p2 bg5">
				<div v-for="(item, i) in tags" :key="i" class="p2 r2 bg2 c5 mr2 flex">
					<div>{{ item.type }}</div>
					<span
						class="cu2 c7 ml2"
						v-if="item.type !== 'plan'"
						@click="removeTagFromPlan(item)"
						>&times;</span
					>
				</div>
			</j-card>

			<!-- Footer -->
			<div class="flex xs-12 xs-right mt6" slot="footer">
				<j-button
					type="error"
					@click="$modal.show('j-dialog')"
					:isLoading="isDeleteLoading"
				>
					<crm-icon name="delete" color="c5" class="mr2" />Delete
				</j-button>
			</div>
		</div>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="deletePlan"
			>{{ $t("errors.are_you_sure_you_want_to_delete_plan") }}</j-dialog
		>

		<!-- Add Tag Modal -->
		<core-modal name="addTag" :headline="$t('add_tag')">
			<div class="bg5 p2">
				<j-base-input-text v-model="tag" size="small" class="mb2" />
				<div class="flex xs-right">
					<j-button size="small" @click="addTagToProduct">
						{{ $t("add") }}
					</j-button>
				</div>
			</div>
		</core-modal>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import JDialog from "@/components/JDialog/"
import JLoadingToast from "@/components/JLoadingToast/"
import CoreSelectLocale from "@/modules/core/components/CoreSelectLocale"
import JCard from "@/components/JCard/"
import CoreModal from "@/modules/core/components/CoreModal"
import JBaseInputText from "@/components/JBaseInputText"

import { i18n } from "@/locales/setup.js"
import PlanInfo from "../components/PlanInfo"
import PricingLogic from "../components/PricingLogic"
import api from "../api"

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		PlanInfo,
		PricingLogic,
		JButton,
		CrmIcon,
		JDialog,
		JLoadingToast,
		CoreSelectLocale,
		JCard,
		CoreModal,
		JBaseInputText
	},

	data() {
		return {
			isLoading: false,
			isDeleteLoading: false,
			data: null,
			selectedLocale: i18n.locale,
			tag: null
		}
	},

	computed: {
		computedProductId() {
			const { plan, type, version } = this.$route.params
			return { plan, type, version }
		},
		tags() {
			return this?.data?.tags || []
		}
	},

	mounted() {
		this.fetchPlanDetail()
	},

	methods: {
		async fetchPlanDetail() {
			const variables = {
				where: {
					id_in: [this.computedProductId]
				}
			}

			this.isLoading = true

			const res = await api.getPlanDetail(variables, this.selectedLocale)

			this.isLoading = false

			this.data = res?.data?.plans[0]
		},

		async deletePlan() {
			this.isDeleteLoading = true
			let res = await api.deletePlan({ id: this.computedProductId })
			this.isDeleteLoading = false

			if (res?.data?.deletePlan?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deletePlan?.errors ||
							res?.data?.deletePlan?.errors_2?.message ||
							this.$t("errors.general")
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("plan_deleted")
					}
				})

			// Delete locales
			const { plan, type } = this.data.id
			let variables = {
				key: `products-${plan}||${type}-name`
			}
			res = await api.deleteAllL10n(variables)

			variables = {
				key: `products-${plan}||${type}-description`
			}
			res = await api.deleteAllL10n(variables)

			this.$router.push({ name: "PlansList" })
		},

		async addTagToProduct() {
			const { plan, version, type } = this?.data?.id
			this.isLoading = true

			const variables = {
				productId: { plan, version, type },
				input: {
					type: this.tag
				}
			}
			const res = await api.addTagToPlan(variables)
			this.isLoading = false

			if (res?.data?.addTagToProduct?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.addTagToProduct?.errors?.[0] ||
							this.$t("errors.general")
					}
				})
			}

			this.tag = null
			this.$modal.hide("addTag")
			this.fetchPlanDetail()
		},

		async removeTagFromPlan(payload) {
			const { plan, version, type } = this?.data?.id

			this.isLoading = true
			const variables = {
				productId: { plan, version, type },
				id: payload?.id
			}
			const res = await api.removeTagFromPlan(variables)
			this.isLoading = false

			if (res?.data?.removeTagFromProduct?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.removeTagFromProduct?.errors?.[0] ||
							this.$t("errors.general")
					}
				})
			}

			this.fetchPlanDetail()
		}
	},

	watch: {
		selectedLocale: {
			handler(o, n) {
				if (o && n) {
					this.fetchPlanDetail()
				}
			}
		}
	}
}
</script>
